/**
 * A deploy replaces every hashed chunk under /assets. A tab opened before it
 * still holds the old index, so the first lazy page it opens afterwards asks
 * for a chunk that is now a 404 and the import throws ("Something went
 * wrong"). Reloading picks up the new index - so do it once, automatically.
 */

const RELOAD_KEY = "hrms:stale-chunk-reload";
/** A second failure this soon after our own reload is a real outage, not a
 * stale tab - don't loop. */
const RELOAD_GUARD_MS = 30_000;

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading chunk [\w-]+ failed/i;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? "");
  return CHUNK_ERROR.test(message);
}

/** Reloads the page unless it already did so in the last 30 seconds (or
 * session storage is unavailable, where a loop could not be ruled out).
 * Returns whether a reload was started. */
export function reloadOnceForStaleChunk(now = Date.now(), reload = () => window.location.reload()): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (last && now - last < RELOAD_GUARD_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  reload();
  return true;
}

/** Vite fires `vite:preloadError` when a lazy chunk (or its CSS) fails to
 * load. */
export function installStaleChunkReload() {
  window.addEventListener("vite:preloadError", () => {
    reloadOnceForStaleChunk();
  });
}
